/**
 * MapOverlayLayer — an <Svg> stacked exactly over the board, sharing its
 * viewBox and pan/zoom transform, for content that changes while you play.
 *
 * Why it exists: react-native-svg on iOS redraws an ENTIRE <Svg> tree whenever
 * any node in it changes, animated props included. Drawn inside the board, a
 * selection fade or an attack animation repainted every territory, decoration
 * and sea route on every frame. On Arctic (44 territories, ~6x Carpathia's path
 * data) that measured ~35ms frames on an iPhone 13, with half of all frames
 * over 33ms while an attack animation played (docs/map-perf.md, 2026-10-04).
 * In its own <Svg> only this small layer redraws, and fades run as a native
 * view-opacity animation that never redraws any SVG.
 *
 * Overlays never take touches by default; the board underneath keeps
 * hit-testing. The tap-slop layer opts into 'box-none'.
 */
import React from 'react'
import Svg, { G } from 'react-native-svg'
import Animated, {
  Easing,
  runOnJS,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated'

type GRef = React.ComponentRef<typeof G>

/**
 * Where an overlay sits and how it pans: everything a layer mounted on demand
 * (a conquest flash) needs to line up with the board. `groupRef` registers the
 * layer's zoom/pan group so a drag moves it with the board.
 */
export interface MapOverlayFrame {
  /** Left offset of the board's <Svg> inside the map wrapper (it is centred). */
  left: number
  width: number
  height: number
  viewBox: string
  /** Same transform the board's zoom/pan group carries. */
  transform: string | undefined
  /** Receives the zoom/pan group so a pan gesture can move it with the board. */
  groupRef: React.Ref<GRef>
}

export interface MapOverlayLayerProps extends MapOverlayFrame {
  style?: React.ComponentProps<typeof Animated.View>['style']
  defs?: React.ReactNode
  children: React.ReactNode
  /** 'none' (default) never takes touches; 'box-none' lets its own shapes take
   *  them while taps on empty space fall through to the board. */
  pointerEvents?: 'none' | 'box-none'
}

export function MapOverlayLayer({ left, width, height, viewBox, transform, groupRef, style, defs, children, pointerEvents = 'none' }: MapOverlayLayerProps) {
  return (
    <Animated.View pointerEvents={pointerEvents} style={[{ position: 'absolute', left, top: 0, width, height }, style]}>
      <Svg width={width} height={height} viewBox={viewBox} pointerEvents={pointerEvents}>
        {defs}
        <G ref={groupRef} transform={transform}>
          {children}
        </G>
      </Svg>
    </Animated.View>
  )
}

export const OVERLAY_FADE_MS = 250

/**
 * Fade an overlay in when `active` turns on and out when it turns off, using
 * native view opacity. While fading out it keeps returning the last active
 * `value`, so the overlay content stays on screen until it is invisible.
 * Swapping content while active (source → target) does not re-fade.
 */
export function useOverlayFade<T>(value: T, active: boolean, instant = false) {
  const reducedMotion = useReducedMotion()
  const duration = instant || reducedMotion ? 0 : OVERLAY_FADE_MS
  const opacity = useSharedValue(active ? 1 : 0)
  const lastActive = React.useRef(value)
  if (active) lastActive.current = value
  const [lingering, setLingering] = React.useState(false)
  // Start lingering in the SAME render that deactivates. Set from an effect,
  // one commit rendered nothing: the selection vanished, then came back (a full
  // redraw of its glows) and faded — a flicker when tapping off a source.
  const [wasActive, setWasActive] = React.useState(active)
  if (active !== wasActive) {
    setWasActive(active)
    setLingering(!active && duration > 0)
  }

  React.useEffect(() => {
    if (active) {
      opacity.value = withTiming(1, { duration, easing: Easing.inOut(Easing.quad) })
      return
    }
    if (opacity.value === 0 || duration === 0) {
      opacity.value = 0
      setLingering(false)
      return
    }
    opacity.value = withTiming(0, { duration, easing: Easing.inOut(Easing.quad) }, (finished) => {
      if (finished) runOnJS(setLingering)(false)
    })
  }, [active, duration, opacity])

  const style = useAnimatedStyle(() => ({ opacity: opacity.value }))
  const shown: T | null = active ? value : lingering ? lastActive.current : null
  return { shown, style }
}
